/**
 * Utility to extract keys of type T that have values of type ValueType.
 *
 * Nullable properties are included, since the filterFunction checks the value type at runtime and skips the items where the property has no value.
 */
type KeysOfType<T, ValueType> = {
	[K in keyof T]: NonNullable<T[K]> extends ValueType ? K : never;
}[keyof T];

type SortFunction<T> = (a: T, b: T) => -1 | 0 | 1;

/** Specifically string-based filtering arrays */
type StringKeysArray<T> = KeysOfType<T, string>[];

/**
 * What a replace() changed, for a listener that can redraw one item instead of all of them.
 *
 * The index is where the replacement landed in the collection after filtering and sorting, or -1 if the filter leaves it out. A listener that draws the collection in order can compare it with what it already drew, and fall back to reading the whole collection when the item has moved.
 */
export interface Replacement<Item> {
	readonly kind: 'replacement';
	/** The item that was in the collection. */
	readonly current: Item;
	/** Where the replacement sits in the collection now, or -1 if the filter leaves it out. */
	readonly index: number;
	/** The item that took its place. */
	readonly replacement: Item;
}

/** An item put in at an index, among the items as they are shown after filtering and sorting. One step of a {@link Reconciliation}. */
export interface Insertion<Item> {
	readonly kind: 'insertion';
	readonly item: Item;
	readonly index: number;
}

/** An item taken out from an index, among the items as they are shown. One step of a {@link Reconciliation}. */
export interface Removal<Item> {
	readonly kind: 'removal';
	readonly item: Item;
	readonly index: number;
}

/** An item moved from one index to another, among the items as they are shown, with the indexes of everything between shifting by one. One step of a {@link Reconciliation}. */
export interface Move<Item> {
	readonly kind: 'move';
	readonly item: Item;
	readonly from: number;
	readonly to: number;
}

/** One step of a {@link Reconciliation}: what a listener does to go on showing the collection without starting over. */
export type ReconciliationStep<Item> = Insertion<Item> | Move<Item> | Removal<Item> | Replacement<Item>;

/**
 * What a reconcile() changed, as the steps that take what was shown to what is shown now, in order.
 *
 * Each step's indexes are right at the moment that step is taken, after the steps before it: a listener that keeps a row per item applies them one by one and ends up showing the collection, with every row that stayed still being the row it was. See {@link ObservableCollection.reconcile}.
 */
export interface Reconciliation<Item> {
	readonly kind: 'reconciliation';
	readonly steps: ReadonlyArray<ReconciliationStep<Item>>;
}

/** What a mutation changed, when the collection can say. */
export type CollectionChange<Item> = Reconciliation<Item> | Replacement<Item>;

/**
 * Told what changed, when the collection knows.
 *
 * Only replace() and reconcile() can say: every other mutation moves items in or out, and a listener cannot do better than reading the collection again. Called with nothing means exactly that.
 */
type UpdateListener<Item> = (change?: CollectionChange<Item>) => void;

/** A fine grained reactive generic Array that can be observed and mutated with explicit methods. */
export default class ObservableCollection<Item> {
	/** The collection Array property contains the sorted and filtered items. */
	public get collection(): Item[] {
		return this.#collection;
	}

	/** Return true or false, if we have any unfiltered items in the Collection. So it will return false if the Collection does not have any items and true if the Collection was instatiated with items or items have been added later. */
	public get containsUnfilteredItems(): boolean {
		return this.#source.length > 0;
	}

	/** Set the filter to the string value you want to search for, in the value of the Item properties you set in filterProperties. */
	public set filter(value: string) {
		if (this.#filter === value) {
			return;
		}
		this.#filter = value.toLowerCase();
		this.#updateCollectionFilterAndSort();
		this.#notifyUpdateListeners();
	}

	public get filter(): string {
		return this.#filter;
	}

	/** When you want to filter the items in the ObservableCollection, set the filterProperties to an Array that has the properties, which values will be included when you set the filter property string. */
	public set filterProperties(value: null | StringKeysArray<Item>) {
		if (this.#filterProperties === value) {
			return;
		}
		this.#filterProperties = value;
		this.#updateCollectionFilterAndSort();
		this.#notifyUpdateListeners();
	}

	public get filterProperties(): null | StringKeysArray<Item> {
		return this.#filterProperties;
	}

	/** The total number of items in the collection. */
	public get length(): number {
		return this.#collection.length;
	}

	/** Sets the sort function, null will remove the sort and invoke update listeners. */
	public set sortFunction(value: null | SortFunction<Item>) {
		if (this.#sortFunction === value) {
			return;
		}
		assertIsValidSortFunction(value);
		this.#sortFunction = value;
		this.#updateCollectionFilterAndSort();
		this.#notifyUpdateListeners();
	}

	public get sortFunction(): null | SortFunction<Item> {
		return this.#sortFunction;
	}

	#collection: Item[];
	#filter: string;
	#filterProperties: null | StringKeysArray<Item>;
	#sortFunction: null | SortFunction<Item>;
	#source: Item[];
	#updateListeners: Set<UpdateListener<Item>>;
	public constructor(items: Item[] = []) {
		assertIsArray(items);
		assertArrayItemsAreUnique(items);
		this.#collection = [];
		this.#filter = '';
		this.#filterProperties = null;
		this.#source = items;
		this.filterFunction = this.filterFunction.bind(this);
		this.#updateListeners = new Set();
		this.#sortFunction = null;
		this.#updateCollectionFilterAndSort();
	}

	/**
	 * Adds a single item to the ObservableCollection.
	 *
	 * Do NOT push items to the collection Array property.
	 *
	 * @param item A value of generic type Item.
	 */
	public addItem(item: Item): void {
		assertItemIsUniqueInCollection(item, this.#source);
		this.#source.push(item);
		this.#updateCollectionFilterAndSort();
		this.#notifyUpdateListeners();
	}

	/**
	 * Adds an Array of items to the ObservableCollection.
	 *
	 * Do NOT push items to the collection Array property.
	 *
	 * @param items An Array with values of generic type Item.
	 */
	public addItems(items: Item[]): void {
		assertArrayItemsAreUnique(items);
		assertCollectionsItemsAreUniqueAcrossCollections([items, this.#source]);
		for (const item of items) {
			this.#source.push(item);
		}
		this.#updateCollectionFilterAndSort();
		this.#notifyUpdateListeners();
	}

	/**
	 * Removes all items, but will not remove filter- or sort functions.
	 *
	 * The subscribeReset() listener will be invoked.
	 */
	public clear(): void {
		this.#collection = [];
		this.#source = [];
		this.#notifyUpdateListeners();
	}

	/** Returns the item at a given index, if an item is not found, null will be returned. */
	public getItemAt(index: number): Item | null {
		const item = this.#source.at(index);
		if (item) {
			return item;
		}
		return null;
	}

	/**
	 * Removes a single item from the ObservableCollection.
	 *
	 * @param item A value of generic type Item.
	 */
	public removeItem(item: Item): void {
		const idx = this.#source.indexOf(item);
		if (idx !== -1) {
			this.#source.splice(idx, 1);
			this.#updateCollectionFilterAndSort();
			this.#notifyUpdateListeners();
		}
	}

	/**
	 * Replaces a single item with another, in the place the first one held.
	 *
	 * The one mutation that tells its listeners what it did, so a listener that draws an item can redraw that one item rather than all of them. See {@link Replacement}.
	 *
	 * Nothing happens if the item is not in the collection, the way removeItem() does nothing, or if it is being replaced with itself.
	 *
	 * @param current An item in the ObservableCollection.
	 * @param replacement The item to put in its place, which must not already be in the ObservableCollection.
	 */
	public replace(current: Item, replacement: Item): void {
		if (current === replacement) {
			return;
		}
		const index = this.#source.indexOf(current);
		if (index === -1) {
			return;
		}
		assertItemIsUniqueInCollection(replacement, this.#source);
		this.#source[index] = replacement;
		this.#updateCollectionFilterAndSort();
		this.#notifyUpdateListeners({
			current,
			index: this.#collection.indexOf(replacement),
			kind: 'replacement',
			replacement,
		});
	}

	/**
	 * Puts a whole new set of items in, the way setItems() does, but tells its listeners how to get from what they showed to what they show now rather than only that it changed.
	 *
	 * For a listener that keeps something per item, such as a list with a row for each: told only that the set changed, it has to throw every row away and build them again, which takes a row the user is in out of the document and loses their focus. Told the steps, it keeps every row whose item is still there, puts in
	 * rows for the items that are new, takes out rows for the ones that are gone, and moves the ones that changed place — see {@link Reconciliation}.
	 *
	 * Items are matched by the key the function answers, since what the new set holds are new objects: a value that is made again rather than changed is never the same object twice, but it is about the same thing, and the key is what says so. An item whose key is in both sets stays, and is replaced where it stands if
	 * it is another object; one whose key is only in the new set is inserted; one whose key is only in the old set is removed. The steps are taken over the items as they are shown, after filtering and sorting, which is what a listener shows.
	 *
	 * Told the same way setItems() tells it, once, in three cases where the steps would say nothing useful: when no key survives, because then every row goes anyway; when a key is not unique on either side, because then the items cannot be matched; and when nothing at all changed, which is said with no telling at all,
	 * the way replacing an item with itself is.
	 *
	 * The items are copied in, the way setItems() copies them.
	 *
	 * @param items The items to hold, which must be unique. An empty array empties the collection.
	 * @param keyOf What tells one item from another across the two sets, answered for every item.
	 */
	public reconcile(items: Item[], keyOf: (item: Item) => unknown): void {
		const replacement = [...items];
		assertArrayItemsAreUnique(replacement);
		const shown = this.#collection;
		this.#source = replacement;
		this.#updateCollectionFilterAndSort();
		const steps = stepsBetween(shown, this.#collection, keyOf);
		if (steps === null) {
			this.#notifyUpdateListeners();
			return;
		}
		if (steps.length === 0) {
			return;
		}
		this.#notifyUpdateListeners({ kind: 'reconciliation', steps });
	}
	/**
	 * Puts a whole new set of items in, in place of everything the collection holds.
	 *
	 * What clear() followed by addItems() does, except that it tells its listeners once instead of twice. That matters where a listener rebuilds something on being told: a list showing this collection would otherwise tear down every row it has and build them all again, only to be told immediately afterwards to build
	 * them a second time. Swapping one set of items for another is one change, and this is the method that says so.
	 *
	 * Filter and sort functions are kept, the way clear() keeps them: they say how the collection is shown, not what is in it.
	 *
	 * The items are copied in, so the array handed over is not the one kept — unlike the constructor, which keeps what it is given and would grow the caller's array from then on.
	 *
	 * @param items The items to hold, which must be unique. An empty array empties the collection, which is what clear() does.
	 */
	public setItems(items: Item[]): void {
		const replacement = [...items];
		assertArrayItemsAreUnique(replacement);
		this.#source = replacement;
		this.#updateCollectionFilterAndSort();
		this.#notifyUpdateListeners();
	}

	/**
	 * Subscribe to ObservableCollection updates which will happen when the collection changes.
	 *
	 * @param listener The listener function with this definition: () => void;
	 */
	public subscribe(listener: UpdateListener<Item>): void {
		assertIsFunction(listener);
		assertIsNotSubscribed(listener, this.#updateListeners);
		this.#updateListeners.add(listener);
	}

	/**
	 * UnSubscribe from the ObservableCollection updates which will happen when the collection changes.
	 *
	 * @param listener The listener function with this definition: () => void that will be removed from being invoked when the collection changes.
	 */
	public unsubscribe(listener: UpdateListener<Item>): void {
		assertIsFunction(listener);
		assertIsSubscribed(listener, this.#updateListeners);
		this.#updateListeners.delete(listener);
	}

	#notifyUpdateListeners(change?: CollectionChange<Item>): void {
		for (const listener of this.#updateListeners) {
			listener(change);
		}
	}

	#updateCollectionFilterAndSort(): void {
		this.#collection = [];
		this.#updateFilter();
		this.#updateSort();
	}

	#updateFilter(): void {
		if (this.#filter) {
			this.#collection = this.#source.filter(this.filterFunction);
		} else {
			// the #collection is empty so we concat #source into #collection
			// [...] spread operator is known to have slow performance, so we use concat()
			this.#collection = this.#collection.concat(this.#source);
		}
	}

	#updateSort(): void {
		if (this.#sortFunction) {
			this.#collection.sort(this.#sortFunction);
		}
	}

	private filterFunction(item: Item): boolean {
		if (this.#filterProperties === null) {
			return true;
		}
		return this.#filterProperties.some((property) => {
			const value = item[property];
			return typeof value === 'string' && value.toLowerCase().includes(this.#filter);
		});
	}
}

function assertArrayItemsAreUnique<Item>(items: Item[]): asserts items is Item[] {
	const set = new Set<Item>(items);
	if (set.size === items.length) {
		return;
	}
	throw new TypeError(`[${String(items)}] is invalid, must be Array of unique values or references`);
}

function assertCollectionsItemsAreUniqueAcrossCollections<Item>(collections: [Item[], Item[]]): asserts collections is [Item[], Item[]] {
	/**
	 * Previous version use Set.intersection() const a = new Set(collections[0]); const b = new Set(collections[1]); const intersection = a.intersection(b); if intersection.size is 0, we don't have duplicates
	 *
	 * We have to wait with Set.intersection until we can bump Typescript to a higher version.
	 */
	const needles = collections[0];
	const haystack = collections[1];
	for (const needle of needles) {
		assertItemIsUniqueInCollection(needle, haystack);
	}
}

function assertIsArray<Item>(value: unknown): asserts value is Item[] {
	if (Array.isArray(value)) {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be an instance of Array`);
}

function assertIsFunction(value: unknown): asserts value is () => void {
	if (typeof value === 'function') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be a type () => void`);
}

function assertIsNotSubscribed(listener: () => void, register: Set<() => void>): asserts listener is () => void {
	if (register.has(listener) === false) {
		return;
	}
	throw new TypeError(`[${String(listener)}] is invalid, is already subscribed`);
}

function assertIsSubscribed(listener: () => void, register: Set<() => void>): asserts listener is () => void {
	if (register.has(listener)) {
		return;
	}
	throw new TypeError(`[${String(listener)}] is invalid, must be subscribed`);
}

function assertIsValidSortFunction<Item>(value: unknown): asserts value is null | SortFunction<Item> {
	if (typeof value === 'function' || value === null) {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be (a: Item, b: Item) => -1 | 0 | 1 or null`);
}

function assertItemIsUniqueInCollection<Item>(value: Item, collection: Item[]): asserts value is Item {
	if (collection.includes(value) === false) {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be unique in the collection`);
}

/**
 * The steps that take one shown list to another, matched by key, or null when the steps would say nothing useful: when no key is in both lists, or when a key is not unique in either. Two empty lists are no steps, not null. See {@link ObservableCollection.reconcile}.
 *
 * Worked out on a copy of the first list that is changed step by step, so each step's indexes are the ones a listener taking the steps in order will find. Removals first, from the end, so the indexes of what is left are not disturbed. Then each wanted item in turn: the item with its key is found among what is left, at
 * or after the place it is wanted at, since everything before that place is settled by then; it is moved there if it is elsewhere, and replaced there if it is another object. A key found nowhere is an insertion.
 */
function stepsBetween<Item>(shown: Item[], wanted: Item[], keyOf: (item: Item) => unknown): Array<ReconciliationStep<Item>> | null {
	const keys = new Map<Item, unknown>();
	const wantedKeys = new Set<unknown>();
	for (const item of wanted) {
		const key = keyOf(item);
		if (wantedKeys.has(key)) {
			return null;
		}
		wantedKeys.add(key);
		keys.set(item, key);
	}
	const shownKeys = new Set<unknown>();
	for (const item of shown) {
		const key = keyOf(item);
		if (shownKeys.has(key)) {
			return null;
		}
		shownKeys.add(key);
		keys.set(item, key);
	}
	// two empty lists are nothing changed rather than no key surviving, which every() would otherwise say of an empty list
	if (shown.length === 0 && wanted.length === 0) {
		return [];
	}
	if (shown.every((item) => !wantedKeys.has(keys.get(item)))) {
		return null;
	}
	const steps: Array<ReconciliationStep<Item>> = [];
	const work = [...shown];
	for (let index = work.length - 1; index >= 0; index -= 1) {
		const item = work[index] as Item;
		if (!wantedKeys.has(keys.get(item))) {
			steps.push({ index, item, kind: 'removal' });
			work.splice(index, 1);
		}
	}
	for (const [index, item] of wanted.entries()) {
		const key = keys.get(item);
		const at = work.findIndex((candidate, place) => place >= index && keys.get(candidate) === key);
		if (at === -1) {
			steps.push({ index, item, kind: 'insertion' });
			work.splice(index, 0, item);
			continue;
		}
		const current = work[at] as Item;
		if (at !== index) {
			work.splice(at, 1);
			work.splice(index, 0, current);
			steps.push({ from: at, item: current, kind: 'move', to: index });
		}
		if (current !== item) {
			steps.push({ current, index, kind: 'replacement', replacement: item });
			work[index] = item;
		}
	}
	return steps;
}
